import React, { useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Paper,
  useTheme,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import { useGetApplicationByIdQuery } from "../services/applicationService";
import { getProductUOMByName } from "../constants/enums";
import LoadingComponent from "./LoadingComponent";
import ErrorComponent from "./ErrorComponent";
import { tokens } from "../theme";
import { localeText } from "../utils/dataGridConfig";
import { formatDate } from "../utils/formatDate";

const ApplicationDetailsModal = ({ applicationId, onClose }) => {
  const { data: applicationDetails, isLoading, error } = useGetApplicationByIdQuery( applicationId, {
    refetchOnMountOrArgChange: true, // Forces refetch when `id` changes
  });
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // State for boomCapacity
  const [boomCapacity, setBoomCapacity] = useState(2000);

  // Add an `id` field to each dosage using the row index
  const dosagesWithIds = applicationDetails?.dosages?.map((dosage, index) => ({
    ...dosage,
    id: index,
  })) || [];

  // Define columns for the DataGrid
  const columns = [
    {
      field: "product_name",
      headerName: "Producto",
      flex: 1,
    },
    {
      field: "dosage_per_boom",
      headerName: "Cantidad por Boom",
      flex: 1,
      renderCell: (params) => {
        const dosageQuantity = params.row?.dosage_quantity || 0;
        const productUOM = params.row?.product_uom || "";
        const uomLabel = getProductUOMByName(productUOM)?.label || "";
        return `${dosageQuantity * ( boomCapacity / 2000 )} ${uomLabel}`;
      },
    },
    {
      field: "total_quantity",
      headerName: "Cantidad Total",
      flex: 1,
      renderCell: (params) => {
        const totalQuantity = params.row?.total_quantity || 0;
        const productUOM = params.row?.product_uom || "";
        const uomLabel = getProductUOMByName(productUOM)?.label || "";
        return `${totalQuantity} ${uomLabel}`;
      },
    },
  ];

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
      }}
    >
      <Paper
        sx={{
          p: 2,
          width: "800px", // Adjust width as needed
          maxHeight: "90vh", // Adjust height as needed
          overflow: "auto",
          backgroundColor: colors.primary[400],
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            alignContent="center"
            sx={{ color: colors.greenAccent[500] }}
          >
            Detalles de la Aplicación:{" "}
            <Box component="span" sx={{ color: "white" }}>
              {applicationDetails.name}
            </Box>
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Application Details */}
        <Box
          display="grid"
          gap="20px"
          gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        >
          {/* Programmed Date */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
              Fecha Programada
            </Typography>
            <Typography variant="body1" color={colors.grey[100]}>
              {formatDate(applicationDetails.programmed_date)}
            </Typography>
          </Box>

          {/* Lote */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
              Lote
            </Typography>
            <Typography variant="body1" color={colors.grey[100]}>
              {applicationDetails.lote_name}
            </Typography>
          </Box>

          {/* Blocks to Apply */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
              Bloques a aplicar
            </Typography>
            <Typography variant="body1" color={colors.grey[100]}>
              {applicationDetails.location}
            </Typography>
          </Box>

          {/* Number of Booms */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
              Cantidad de Booms
            </Typography>
            <Typography variant="body1" color={colors.grey[100]}>
              {applicationDetails.total_area * ( 2000 / boomCapacity )}
            </Typography>
          </Box>

          {/* Hectáreas */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
              Hectáreas
            </Typography>
            <Typography variant="body1" color={colors.grey[100]}>
              {applicationDetails.total_area}
            </Typography>
          </Box>

          {/* Boom Capacity Selector */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="boom-capacity-label" sx={{ color: colors.grey[100] }}>
                Capacidad del Boom
              </InputLabel>
              <Select
                labelId="boom-capacity-label"
                value={boomCapacity}
                label="Capacidad"
                onChange={(e) => setBoomCapacity(e.target.value)}
                sx={{
                  backgroundColor: colors.primary[500],
                  color: colors.grey[100],
                  width: "120px", // Adjust width to make it smaller
                  "& .MuiSelect-select": {
                    padding: "8px 12px", // Adjust padding to make it compact
                  },
                }}
              >
                <MenuItem value={2000}>2000 L</MenuItem>
                <MenuItem value={4000}>4000 L</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Divider */}
        <Divider sx={{ my: "20px" }} />

        {/* Dosification */}
        <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
          Dosificación
        </Typography>

        {/* DataGrid for Dosages */}
        <Box
          height="40vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              fontSize: "0.9rem",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[500],
            },
            "& .MuiDataGrid-withBorderColor": {
              backgroundColor: colors.primary[500],
              fontSize: "1.1rem",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
            },
            "& ::-webkit-scrollbar": {
              width: "5px"
            },
            "& ::-webkit-scrollbar-track": {
              backgroundColor: colors.primary[400]
            },
            "& ::-webkit-scrollbar-thumb": {
              borderRadius: "10px",
              backgroundColor: colors.greenAccent[400]
            },
          }}
        >
          <DataGrid
            rows={dosagesWithIds}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            disableColumnMenu
            disableColumnSorting
            hideFooterSelectedRowCount
            pageSizeOptions={[100]}
            loadingText="Cargando..."
            localeText={localeText}
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ApplicationDetailsModal;